import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import decodeHtmlEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import type { ReferencesMap } from '@fogia/core-web/utils/internalRefToSlug';

import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { buildOgImageUrl } from '@fogia/core-web/libs/sanity/imageUrl';

import formatMetaDescription from '@fogia/core-web/components/metadata/utils/formatMetaDescription';

// Page types that must never be indexed (system/dynamic pages), regardless of CMS settings.
// @TODO: This should be moved to config when the structure is set
const NO_INDEX_PAGE_TYPES: string[] = ['searchPage', 'notFoundPage'];

// @TODO: This should be moved to config when the structure is set
const NO_FOLLOW_PAGE_TYPES: string[] = [];

// Listing pages where query params represent filtering/sorting. Such filtered views must be
// noindex,follow with no canonical (Google does not always honor a canonical to the clean URL).
const LISTING_PAGE_TYPES: string[] = ['categoryPage', 'searchPage'];

// Query params that should NOT flag a listing page as a filtered view.
// `page` is pagination, not a filter (no noindex). Canonical still always points at page 1.
// Tracking/campaign params (utm_*, gclid, etc.) must not deindex or drop the canonical.
const NON_FILTER_QUERY_PARAMS: Set<string> = new Set([
    'page',
    'gclid',
    'gclsrc',
    'dclid',
    'fbclid',
    'msclkid',
    'mc_cid',
    'mc_eid',
    '_ga',
]);

const isNonFilterQueryParam = (key: string): boolean => NON_FILTER_QUERY_PARAMS.has(key) || key.startsWith('utm_');

const hasFilterQueryParams = (search: string): boolean => {
    if (!search) {
        return false;
    }
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    for (const key of params.keys()) {
        if (!isNonFilterQueryParam(key)) {
            return true;
        }
    }
    return false;
};

const ensureString = (value: unknown): string => (typeof value === 'string' ? value : '');

interface PageSeoOpenGraph {
    title?: string;
    description?: string;
    /** Sanity image object (asset + optional crop/hotspot). OG URL built via buildOgImageUrl. */
    image?: { asset?: { _ref?: string; ref?: string }; crop?: unknown; hotspot?: unknown };
}

interface PageSeoRobots {
    noIndex?: boolean;
    noFollow?: boolean;
}

interface PageSeo {
    title?: string;
    description?: string;
    robots?: PageSeoRobots;
    openGraph?: PageSeoOpenGraph;
}

interface PageDataFromState {
    title?: string;
    template?: string;
    settings?: { title?: string };
    seo?: PageSeo;
    references?: ReferencesMap;
}

const PageMeta = () => {
    const applicationDomain = useAppSelector(({ application }) => application?.site?.domain ?? '');
    const siteProtocol = useAppSelector(({ application }) => application?.site?.protocol || '');

    // Page data
    const pageData = useAppSelector(({ page }) => page?.data) as PageDataFromState | undefined;
    const resolvedSlug = useAppSelector(({ page }) => page?.resolvedSlug ?? '');
    // `type` lives at the top level of the page slice (not inside page.data).
    const pageType = useAppSelector(({ page }) => page?.type ?? '');

    // Live URL query string. We read it from the router (not page.search) because filter/sort
    // changes update the URL without re-resolving the page on the client.
    const { search } = useLocation();

    const pageSeo = pageData?.seo;
    const pageSeoTitle = pageSeo?.title ?? '';
    const pageTitle = pageData?.settings?.title ?? '';
    const pageSeoDescription = pageSeo?.description ?? '';

    const pageSeoOpenGraphData = (pageSeo?.openGraph ?? {}) as PageSeoOpenGraph;
    // Editor-controlled robots directives from the Sanity SEO group. Default is index/follow.
    const pageNoIndex = pageSeo?.robots?.noIndex === true;
    const pageNoFollow = pageSeo?.robots?.noFollow === true;

    const ogTitle = pageSeoOpenGraphData?.title;
    const ogDescription = pageSeoOpenGraphData?.description;
    const pageSeoOpenGraphImage = buildOgImageUrl(pageSeoOpenGraphData?.image) ?? '';

    // A filtered/sorted listing view (e.g. category page with filter params). These must be
    // noindex,follow and must not emit a canonical tag.
    const isFilteredListing = LISTING_PAGE_TYPES.includes(pageType) && hasFilterQueryParams(search);

    // Missing robots data always means index/follow. Never invert this logic: absence of data
    // must never result in noindex, or an unsynced/empty document could deindex the whole site.
    const isNoIndex = NO_INDEX_PAGE_TYPES.includes(pageType) || pageNoIndex || isFilteredListing;
    const isNoFollow = NO_FOLLOW_PAGE_TYPES.includes(pageType) || pageNoFollow;
    // Only emit a robots tag when there is something to restrict; default pages stay clean (no tag,
    // which equals index,follow). When emitted, both directives are explicit (e.g. "noindex, follow").
    const robots =
        isNoIndex || isNoFollow
            ? `${isNoIndex ? 'noindex' : 'index'}, ${isNoFollow ? 'nofollow' : 'follow'}`
            : undefined;

    // Drop the canonical on filtered listing views (empty string = not rendered).
    // Note: react-helmet-async won't remove an already SSR-rendered canonical on client-side nav,
    // but that's cosmetic – SSR (what crawlers see) correctly ships no canonical for filtered URLs.
    const canonical =
        !isFilteredListing && resolvedSlug && applicationDomain
            ? `${siteProtocol}${applicationDomain}${resolvedSlug}`
            : '';
    const title = decodeHtmlEntities(String(pageSeoTitle || pageTitle || '')) ?? '';
    const description = formatMetaDescription(String(pageSeoDescription ?? '')) as string;
    const ogTitleStr = String(ogTitle ?? title ?? '');
    const ogDescStr = String(ogDescription ?? description ?? '');
    const safeOgTitle = ensureString(ogTitleStr);
    const safeOgDesc = ensureString(ogDescStr);
    const ogTitleFinal = ogTitleStr ? decodeHtmlEntities(safeOgTitle) : '';
    const ogDescriptionFinal = ogDescStr ? formatMetaDescription(safeOgDesc) : '';

    return (
        <Helmet>
            {/* General meta data */}
            {title && <title>{title}</title>}
            {canonical && <link rel="canonical" href={canonical} />}
            {description && <meta name="description" content={description} />}
            {robots && <meta name="robots" content={robots} />}

            {/* OpenGraph data */}
            {ogTitleFinal && <meta property="og:title" content={ogTitleFinal} />}
            {ogDescriptionFinal && <meta property="og:description" content={ogDescriptionFinal} />}
            {pageSeoOpenGraphImage && <meta property="og:image" content={pageSeoOpenGraphImage} />}

            {/* Twitter Card (same values as Open Graph) */}
            {ogTitleFinal && <meta name="twitter:title" content={ogTitleFinal} />}
            {ogDescriptionFinal && <meta name="twitter:description" content={ogDescriptionFinal} />}
            {pageSeoOpenGraphImage && <meta name="twitter:image" content={pageSeoOpenGraphImage} />}
        </Helmet>
    );
};

export default PageMeta;
